import React, { ReactElement, useState } from 'react';
import Kendra from 'aws-sdk/clients/kendra';
import { attributeNameToFacetTitle } from '../utils';
import { FacetCheckbox } from './FacetCheckbox';
import './Facet.scss';
import { FacetValue } from './types';
import { SelectedFacetRetriever } from '../SelectedFacetRetriever';
import _ from 'lodash';
import { useTranslate } from 'src/i18n/useTranslate';

interface Props {
    attributeName: string;
    values: FacetValue[];
    disableAdd: boolean;

    facetConfiguration: {
        facetsToShowWhenUncollapsed: number;
        showCount: boolean;
    };

    onSelectionChange: (
        key: Kendra.DocumentAttributeKey,
        value: Kendra.DocumentAttributeValue,
        changeDetail: boolean
    ) => void;

    onClear: (key: Kendra.DocumentAttributeKey) => void;

    selectedFacets?: SelectedFacetRetriever;
}

export const Facet = (props: Props): ReactElement => {
    const t = useTranslate();
    const [expanded, setExpanded] = useState(false);
    const showLess = () => {
        setExpanded(false);
    };

    const showMore = () => {
        setExpanded(true);
    };

    const onClickClearButton = () => {
        props.onClear(props.attributeName);
    };

    const {
        attributeName,
        disableAdd,
        facetConfiguration,
        values,
        selectedFacets,
    } = props;

    const expandable =
        values.length > facetConfiguration.facetsToShowWhenUncollapsed;

    const hasSomeValuesSelected = !_.isEmpty(
        selectedFacets?.getStringSelectionsOf(attributeName)
    );

    return (
        <div className="facet">
            <div className="facet-title">
                {attributeNameToFacetTitle(attributeName)}&nbsp;
                {hasSomeValuesSelected && (
                    <span onClick={onClickClearButton} className="action-link">
                        {t('clear', 'Clear')}
                    </span>
                )}
            </div>
            <div className="facet-value-container">
                {values.map((value, idx) => {
                    if (!value.DocumentAttributeValue) {
                        return null;
                    }

                    const show =
                        expanded ||
                        idx < facetConfiguration.facetsToShowWhenUncollapsed;

                    const isSelected = selectedFacets
                        ? selectedFacets.isSelected(
                              attributeName,
                              value.DocumentAttributeValue
                          )
                        : false;

                    if (show || isSelected) {
                        return (
                            <FacetCheckbox
                                key={idx}
                                attributeName={attributeName}
                                value={value.DocumentAttributeValue}
                                valueLabel={value.ValueLabel}
                                count={value.Count}
                                selected={isSelected}
                                disableAdd={disableAdd}
                                onSelectionChange={props.onSelectionChange}
                                facetConfiguration={facetConfiguration}
                            />
                        );
                    } else {
                        return null;
                    }
                })}
            </div>
            <div className="facet-footer">
                {expandable && expanded && (
                    <span className="action-link" onClick={showLess}>
                        {t('show_less', 'Show Less')}
                    </span>
                )}
                {expandable && !expanded && (
                    <span className="action-link" onClick={showMore}>
                        {t('show_more', 'Show More')}
                    </span>
                )}
            </div>
        </div>
    );
};
